import { useRef, useState } from "react";
import { api } from "../api";
import { resizeImage } from "../lib/imageResize";

/**
 * Pick a logo, see it, upload it.
 *
 * The image is resized in the browser first. A coach picks whatever their phone
 * took, and the API caps uploads at 512KB — rejecting a 4MB photo and telling
 * them to find an image editor would be a dead end for something displayed at
 * about 40 pixels.
 */
export function ClubLogoUpload({
  clubId,
  clubName,
  logoUrl,
  onChanged,
}: {
  clubId: string;
  clubName: string;
  logoUrl: string | null;
  onChanged: () => void | Promise<void>;
}) {
  const fileInput = useRef<HTMLInputElement>(null);
  const [preview, setPreview] = useState<string | null>(null);
  const [pending, setPending] = useState<{ dataBase64: string; contentType: string } | null>(null);
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState<{ ok: boolean; text: string } | null>(null);

  async function handlePick(file: File) {
    setMessage(null);
    try {
      const resized = await resizeImage(file);
      setPreview(resized.previewUrl);
      setPending({ dataBase64: resized.dataBase64, contentType: resized.contentType });
      setMessage({
        ok: true,
        text: `Ready to upload — ${Math.max(1, Math.round(resized.bytes / 1024))}KB after resizing.`,
      });
    } catch (err) {
      setPreview(null);
      setPending(null);
      setMessage({ ok: false, text: err instanceof Error ? err.message : "Couldn't read that image." });
    }
  }

  async function handleUpload() {
    if (!pending) return;
    setBusy(true);
    setMessage(null);
    try {
      await api(`/v1/clubs/${clubId}/logo`, { method: "POST", body: JSON.stringify(pending) });
      setPending(null);
      setPreview(null);
      if (fileInput.current) fileInput.current.value = "";
      await onChanged();
      setMessage({ ok: true, text: "Logo updated." });
    } catch (err) {
      setMessage({ ok: false, text: err instanceof Error ? err.message : "Upload failed" });
    } finally {
      setBusy(false);
    }
  }

  async function handleRemove() {
    if (!window.confirm(`Remove ${clubName}'s logo?`)) return;
    setBusy(true);
    setMessage(null);
    try {
      await api(`/v1/clubs/${clubId}/logo`, { method: "DELETE" });
      setPending(null);
      setPreview(null);
      await onChanged();
      setMessage({ ok: true, text: "Logo removed." });
    } catch (err) {
      setMessage({ ok: false, text: err instanceof Error ? err.message : "Remove failed" });
    } finally {
      setBusy(false);
    }
  }

  // The pending pick wins over the stored logo, so the preview shows what will
  // happen rather than what already is.
  const shown = preview ?? logoUrl;

  return (
    <div className="rounded-xl border border-slate-200 bg-white p-4">
      <h3 className="mb-1 text-sm font-semibold">Logo</h3>
      <p className="mb-4 text-xs text-slate-500">
        Shown next to the club's name. Large images are resized automatically — PNG keeps a
        transparent background.
      </p>

      <div className="flex flex-wrap items-center gap-4">
        <div className="flex h-20 w-20 items-center justify-center overflow-hidden rounded-lg border border-slate-200 bg-slate-50">
          {shown ? (
            <img src={shown} alt={`${clubName} logo`} className="max-h-full max-w-full object-contain" />
          ) : (
            <span className="text-xs text-slate-400">None</span>
          )}
        </div>

        <div className="flex flex-col gap-2">
          <input
            ref={fileInput}
            type="file"
            accept="image/png,image/jpeg,image/webp,image/*"
            onChange={(e) => {
              const f = e.target.files?.[0];
              if (f) void handlePick(f);
            }}
            className="text-sm"
          />
          <div className="flex items-center gap-3">
            <button
              type="button"
              onClick={handleUpload}
              disabled={busy || !pending}
              className="rounded-md bg-brand-600 px-4 py-1.5 text-sm font-semibold text-white hover:bg-brand-700 disabled:opacity-50"
            >
              {busy ? "Working…" : "Upload logo"}
            </button>
            {logoUrl && !pending && (
              <button
                type="button"
                onClick={handleRemove}
                disabled={busy}
                className="text-xs font-medium text-rose-600 hover:underline disabled:opacity-50"
              >
                Remove
              </button>
            )}
          </div>
        </div>
      </div>

      {message && (
        <p className={`mt-3 text-sm ${message.ok ? "text-emerald-600" : "text-rose-600"}`}>
          {message.text}
        </p>
      )}
    </div>
  );
}
